import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { updateIncidentCategoryInput } from "@alm/validation";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RowActions } from "@/components/app/patterns/row-actions";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { RecordRow } from "@/components/app/lists/record-row";
import { useBuildingInView } from "@/components/app/rails/settings-scope-line";
import { SharedRowNote } from "@/components/app/lists/shared-row-note";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useNewRowPlace } from "@/hooks/use-new-row-place";
import { useFacility } from "@/lib/facility/facility";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { useCan, useCanChangeRow, usePolicy } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { CatalogEntryEditDialog } from "./-components/care/catalog-entry-edit-dialog";
import { IncidentCategoryCreateForm } from "./-components/care/incident-category-create-form";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";

export const Route = createFileRoute(
  "/_authenticated/_administration/settings/incident-categories",
)({
  beforeLoad: ({ context }) => guardRoute(context.me, "incident-categories"),
  component: IncidentCategoriesSettings,
});

const fail = (error: Error) => toast.error(errorWords(error));

type Category = Output<typeof orpcClient.care.incidents.categories.list>[number];

const ADMIN_HERE =
  "Changing a category takes the Administrator actions permission at its building.";
const ADMIN_EVERYWHERE =
  "Shared by every building, so only someone with the Administrator actions permission for the whole organization can change it.";

// The shared schema's bare min(1) would say "Too small"; the form says the fix.
const categoryEdit = updateIncidentCategoryInput.omit({ categoryId: true }).extend({
  name: z.string().trim().min(1, "Enter a name, like Fall.").max(120).optional(),
});

/** Standard categories are read-only here: they are shared by every operator, so switching one off
 * would remove it from everybody's form. */
function IncidentCategoriesSettings() {
  const { facilityId } = useFacility();
  const realFacility = facilityId === "all" ? null : facilityId;
  const canManage = useCan("incident:manage");
  // Keeping the list is an administrator's act at the category's building; one for every
  // building is the organization's administrators'. Staff with incident:manage still need it.
  const { can } = usePolicy();
  const administers = useCanChangeRow("organization:administer");
  const changeable = (row: Category) =>
    !row.key && can("incident:manage", row.facilityId) && administers(row.facilityId);
  const place = useNewRowPlace("organization:administer");
  const canAdd =
    canManage &&
    (realFacility ? administers(realFacility) : place.everyBuilding || place.buildings.length > 0);
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [removing, setRemoving] = useState<Category | null>(null);

  /* Named, the API answers with that building's own rows only; the incident form there also
     offers the organization's and the standard ones, so ask for all and keep what it offers. */
  const categories = useQuery(
    orpc.care.incidents.categories.list.queryOptions({
      input: { facilityId: null, includeInactive: true },
    }),
  );
  const building = useBuildingInView();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.care.incidents.categories.key() });

  const create = useMutation(
    orpc.care.incidents.categories.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Category added");
        setAdding(false);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const update = useMutation(
    orpc.care.incidents.categories.update.mutationOptions({
      onSuccess: async () => {
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.care.incidents.categories.remove.mutationOptions({
      onSuccess: async (result) => {
        toast.success(result.deactivated ? "Category switched off" : "Category deleted");
        setRemoving(null);
        await invalidate();
      },
      onError: (error: Error) => {
        setRemoving(null);
        fail(error);
      },
    }),
  );

  const rows = (categories.data ?? [])
    .filter((row) => !realFacility || row.facilityId === null || row.facilityId === realFacility)
    .toSorted((a, b) => a.name.localeCompare(b.name));
  const offered = rows.filter((row) => row.isActive).length;

  return (
    <>
      <PageHeader
        title="Incident categories"
        meta="The choices staff pick from to say what kind of incident happened when they report one."
      />
      <div className="flex flex-col gap-3">
        <RecordCard
          title="Categories"
          count={categories.data ? rows.length : undefined}
          canWrite={canAdd}
          addLabel="Add category"
          onAdd={() => setAdding(true)}
        >
          {categories.isPending ? (
            <RowsSkeleton />
          ) : categories.isError ? (
            <ReadFailed
              message="The incident categories could not be loaded."
              error={categories.error}
              retrying={categories.isFetching}
              onRetry={() => void categories.refetch()}
            />
          ) : rows.length === 0 ? (
            <EmptyRows text="No categories yet. Add one so staff can classify an incident when they log it." />
          ) : (
            <ul className="divide-y">
              {rows.map((row) => (
                <RecordRow
                  key={row.id}
                  title={
                    <span className="flex flex-wrap items-center gap-2">
                      {row.name}
                      {row.key ? <Badge variant="outline">Standard</Badge> : null}
                      {row.isActive ? null : <Badge variant="secondary">Off</Badge>}
                    </span>
                  }
                  meta={
                    row.key
                      ? "Standard, shared by every operator"
                      : row.facilityId
                        ? row.facilityId === realFacility
                          ? `${building.name ?? "This building"} only`
                          : "One building"
                        : "Every building"
                  }
                  actions={
                    changeable(row) ? (
                      <RowActions
                        modal={false}
                        label={row.name}
                        primary={{ label: "Edit", onSelect: () => setEditing(row) }}
                        items={[
                          {
                            label: row.isActive ? "Switch off" : "Switch on",
                            disabled: update.isPending,
                            onSelect: () =>
                              update.mutate({ categoryId: row.id, isActive: !row.isActive }),
                          },
                          {
                            label: "Delete category",
                            destructive: true,
                            onSelect: () => setRemoving(row),
                          },
                        ]}
                      />
                    ) : canManage && !row.key ? (
                      <SharedRowNote reason={row.facilityId ? ADMIN_HERE : ADMIN_EVERYWHERE} />
                    ) : null
                  }
                />
              ))}
            </ul>
          )}
        </RecordCard>
        {categories.data && rows.length > 0 ? (
          <p className="text-muted-foreground text-sm tabular-nums">
            {offered} of {rows.length} offered in the incident category picker
            {realFacility ? ` at ${building.name ?? "this building"}` : " across every building"}.
          </p>
        ) : null}
      </div>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add category</DialogTitle>
          </DialogHeader>
          <IncidentCategoryCreateForm
            facilityId={realFacility ?? place.startAt}
            place={realFacility ? undefined : place}
            pending={create.isPending}
            onCancel={() => setAdding(false)}
            onSubmit={(values) => create.mutate(values)}
          />
        </DialogContent>
      </Dialog>

      <CatalogEntryEditDialog
        title="Edit category"
        entry={editing}
        schema={categoryEdit}
        saveLabel="Save category"
        pending={update.isPending}
        onClose={() => setEditing(null)}
        onSubmit={(values, categoryId) => update.mutate({ ...values, categoryId })}
      />

      <AlertDialog open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{removing?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Incidents already filed keep the category they were filed under, but it is no longer
              offered.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => removing && remove.mutate({ categoryId: removing.id })}
            >
              Delete category
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
